import React, { useState, useRef, useEffect } from "react";
import { Controller, Control, FieldError } from "react-hook-form";
import DatePicker from "react-datepicker";
import "../ContentPages/style.css";
import "react-datepicker/dist/react-datepicker.css";
import FieldErrorComponent from "../FieldError";

interface FloatingDatePickerProps {
  name: string;
  label: string;
  control: Control<any>;
  error?: FieldError;
  calendarIcon: string;
  required?: boolean;
  maxDate?: Date;
}

const FloatingDateField: React.FC<FloatingDatePickerProps> = ({
  name,
  label,
  control,
  error,
  calendarIcon,
  required = true,
  maxDate = new Date(),
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // helper to format as yyyy-MM-dd in **local time**
  const toLocalYMD = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (showPicker) {
      // Focus the calendar when it opens
      setTimeout(() => {
        const selectedDay =
          document.querySelector(".react-datepicker__day--selected") ||
          document.querySelector(".react-datepicker__day--today") ||
          document.querySelector(".react-datepicker__day");
        if (selectedDay) {
          (selectedDay as HTMLElement).focus();
        }
      }, 0);
    }
  }, [showPicker]);

  // Ref to store the last valid value
  const lastValidValueRef = useRef("");

  const formatDate = (date) => {
    if (!date) return;
    const input = document.getElementById(name) as HTMLInputElement;
    if (!input) return;
    const validDateFormat = /^(\d+\/\d+\/\d+|\/\d+\/\d+)$/;
    const strictDateFormat = /^\d{2}\/\d{2}\/\d{4}$/;
    const isPrevValid = strictDateFormat.test(lastValidValueRef.current);
    const isNewValid = validDateFormat.test(date);

    // If new value is valid and <= 10 chars, allow it and update last valid
    if (isNewValid && date.length <= 10) {
      const [d, m, y] = date.split("/");
      if (d.length <= 2 && m.length <= 2 && y.length <= 4) {
        input.value = date;
        if (strictDateFormat.test(date)) {
          lastValidValueRef.current = date;
        }
        return;
      }
    }

    // If new value is valid but too long, and previous is valid, revert
    if (isNewValid && date.length > 10 && isPrevValid) {
      setTimeout(() => {
        input.value = lastValidValueRef.current;
        input.setSelectionRange(
          lastValidValueRef.current.length,
          lastValidValueRef.current.length
        );
      }, 0);
      return;
    }

    let value = date.replace(/\D/g, ""); // Remove all non-digits
    if (value.length > 8) value = value.slice(0, 8);

    let formatted = value;
    if (value.length > 4) {
      formatted = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4)}`;
    } else if (value.length > 2) {
      formatted = `${value.slice(0, 2)}/${value.slice(2)}`;
    }

    input.value = formatted;
    // If formatted is a strict valid date, update last valid
    if (strictDateFormat.test(formatted)) {
      lastValidValueRef.current = formatted;
    }
  };

  const handleChangeRaw = (value) => {
    console.log(value);
  };

  return (
    <>
      <div
        className={`date-wrapper field-wrapper mb-16 ${
          error ? "error-field" : ""
        }`}
        ref={pickerRef}
        style={{ position: "relative" }}
      >
        <label className="field-label" htmlFor={name}>
          {label} {required && <span className="req-text">*</span>}
        </label>

        <Controller
          name={name}
          control={control}
          rules={{
            required: required ? "This field is required" : false,
            validate: (value) => {
              if (!value && required) return "This field is required";

              // parse safely in local time
              const date = value ? new Date(`${value}T00:00:00`) : null;

              if (!date || isNaN(date.getTime())) return "Invalid date format.";
              if (date > maxDate) return "Future dates are not allowed.";

              return true;
            },
          }}
          render={({ field: { onChange, onBlur, value } }) => {
            // parse back from yyyy-MM-dd safely
            const selectedDate = value ? new Date(`${value}T00:00:00`) : null;

            return (
              <>
                <DatePicker
                  id={name}
                  placeholderText="dd/mm/yyyy"
                  popperPlacement="bottom-end"
                  selected={selectedDate}
                  onBlur={() => {
                    onBlur();
                  }}
                  onChange={(date) => {
                    onChange(date ? toLocalYMD(date) : "");
                    setShowPicker(false);
                    onBlur();
                  }}
                  onChangeRaw={(event) => formatDate(event.target.value)}
                  dateFormat="dd/MM/yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  maxDate={maxDate}
                  className="common-field"
                  open={showPicker}
                  onClickOutside={() => {
                    setShowPicker(false);
                    onBlur();
                  }}
                  onCalendarClose={() => {
                    onBlur();
                  }}
                  onSelect={() => setShowPicker(false)}
                  shouldCloseOnSelect
                />

                <button
                  type="button"
                  onClick={() => setShowPicker((v) => !v)}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                  aria-label="Toggle calendar"
                >
                  <img src={calendarIcon} alt="Calendar Icon" />
                </button>
              </>
            );
          }}
        />
      </div>
      {error && <FieldErrorComponent error={error} info={null} />}
    </>
  );
};

export default FloatingDateField;
